import { buildAssetUrl, decodeBase64Utf8, } from '#pplx/frontend/apps/ask/canvas-shared/utils/assetContentUtils.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
/**
 * Supported audio file extensions (without the leading dot).
 */
const AUDIO_EXTENSIONS = ['mp3', 'wav', 'ogg', 'flac', 'aac', 'm4a', 'webm', 'opus'];
const DOWNLOAD_IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg'];
/**
 * Supported audio MIME types.
 */
const AUDIO_MIME_TYPES = [
    'audio/mpeg',
    'audio/wav',
    'audio/wave',
    'audio/x-wav',
    'audio/ogg',
    'audio/flac',
    'audio/aac',
    'audio/mp4',
    'audio/webm',
];
/**
 * Check if a file is an audio file based on mime type or filename.
 */
export function isAudioFile(mimeType, filename) {
    const lowerMimeType = mimeType.toLowerCase();
    const lowerFilename = filename.toLowerCase();
    if (AUDIO_MIME_TYPES.some((type) => lowerMimeType === type)) {
        return true;
    }
    return AUDIO_EXTENSIONS.some((ext) => lowerFilename.endsWith(`.${ext}`));
}
/**
 * Mapping of file extensions to their corresponding icon definitions.
 */
const FILE_EXTENSION_ICONS = {
    xlsx: $icon('file-type-xls'),
    csv: $icon('file-type-csv'),
    pdf: $icon('file-type-pdf'),
    docx: $icon('file-type-doc'),
    pptx: $icon('file-type-ppt'),
    zip: $icon('file-type-zip'),
    json: $icon('file-type-js'),
    sql: $icon('file-type-sql'),
    yaml: $icon('file-code'),
    yml: $icon('file-code'),
    md: $icon('file-text'),
    html: $icon('file-type-html'),
    ...Object.fromEntries(AUDIO_EXTENSIONS.map((ext) => [ext, $icon('file-music')])),
};
/**
 * Only add stable format names (acronyms or proper names such as Markdown).
 * Other non-acronym values need dedicated localized messages.
 */
const FILE_EXTENSION_FORMAT_TEXT = {
    xlsx: 'XLSX',
    csv: 'CSV',
    pdf: 'PDF',
    docx: 'DOCX',
    pptx: 'PPTX',
    zip: 'ZIP',
    html: 'HTML',
    md: 'Markdown',
};
/**
 * Get the appropriate icon for a file type based on its extension.
 * Returns the caller's fallback, defaulting to a generic file icon, for unrecognized extensions.
 */
export function getIconForFileType(extension, fallback = $icon('file')) {
    const key = extension?.toLowerCase();
    return ((key && Object.hasOwn(FILE_EXTENSION_ICONS, key) ? FILE_EXTENSION_ICONS[key] : undefined) ??
        fallback);
}
export function getDownloadLabelFileType(assetType, extension) {
    if (!extension) {
        return getDefaultDownloadLabelFileType(assetType);
    }
    const normalizedExtension = extension.toLowerCase();
    if (assetType === 'RESEARCH_REPORT' && normalizedExtension === 'zip') {
        return { kind: 'format', fileType: 'Markdown' };
    }
    if (DOWNLOAD_IMAGE_EXTENSIONS.some((imageExtension) => imageExtension === normalizedExtension)) {
        return { kind: 'image' };
    }
    if (AUDIO_EXTENSIONS.some((audioExtension) => audioExtension === normalizedExtension)) {
        return { kind: 'audio' };
    }
    const fileType = Object.hasOwn(FILE_EXTENSION_FORMAT_TEXT, normalizedExtension)
        ? FILE_EXTENSION_FORMAT_TEXT[normalizedExtension]
        : undefined;
    if (!fileType) {
        return getDefaultDownloadLabelFileType(assetType);
    }
    return { kind: 'format', fileType };
}
/**
 * Get display text for a file type based on its extension.
 * Falls back to "HTML" for SLIDES/APP assets or "File" for unknown extensions.
 */
export function getTextForFileType(assetType, extension) {
    const fileType = getDownloadLabelFileType(assetType, extension);
    switch (fileType.kind) {
        case 'file':
            return 'File';
        case 'image':
            return 'Image';
        case 'audio':
            return 'Audio';
        case 'format':
            return fileType.fileType;
    }
}
function getDefaultDownloadLabelFileType(assetType) {
    if (assetType === 'SLIDES' || assetType === 'APP') {
        return { kind: 'format', fileType: 'HTML' };
    }
    return { kind: 'file' };
}
const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'ico'];
const VIDEO_EXTENSIONS = ['mp4', 'webm', 'mov', 'avi', 'mkv'];
function getExtension(filename) {
    const lastDot = filename.lastIndexOf('.');
    return lastDot >= 0 ? filename.slice(lastDot + 1).toLowerCase() : '';
}
/**
 * Build an Asset from file metadata based on mime type.
 * Falls back to filename extension if mime type doesn't match known types.
 */
export function buildAssetFromFile({ filename, mimeType, content, url, uuid, size, }) {
    const lowerMime = mimeType.toLowerCase();
    const ext = getExtension(filename);
    const isImage = lowerMime.startsWith('image/') || IMAGE_EXTENSIONS.some((imgExt) => ext === imgExt);
    if (isImage) {
        return {
            uuid,
            asset_type: 'GENERATED_IMAGE',
            generated_image: { url: buildAssetUrl({ content, mimeType, url }) },
        };
    }
    const isVideo = lowerMime.startsWith('video/') || VIDEO_EXTENSIONS.some((vidExt) => ext === vidExt);
    if (isVideo) {
        return {
            uuid,
            asset_type: 'GENERATED_VIDEO',
            generated_video: { url: buildAssetUrl({ content, mimeType, url }) },
        };
    }
    if (lowerMime === 'application/pdf' || ext === 'pdf') {
        return {
            uuid,
            asset_type: 'PDF_FILE',
            pdf_file: { url: buildAssetUrl({ content, mimeType, url }), filename, file_size: size },
        };
    }
    if (lowerMime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        ext === 'docx') {
        return {
            uuid,
            asset_type: 'DOCX_FILE',
            docx_file: { url: buildAssetUrl({ content, mimeType, url }), filename, file_size: size },
        };
    }
    if (lowerMime === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        ext === 'xlsx') {
        return {
            uuid,
            asset_type: 'XLSX_FILE',
            xlsx_file: { url: buildAssetUrl({ content, mimeType, url }), filename, file_size: size },
        };
    }
    const textContent = content ? decodeBase64Utf8(content) : '';
    return {
        uuid,
        asset_type: 'CODE_FILE',
        download_info: [{ filename, url: buildAssetUrl({ content, mimeType, url }) }],
        code_file: { filename, content: textContent, mime_type: mimeType },
    };
}
//# sourceMappingURL=fileTypeUtils.js.map